import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  DocumentTextIcon,
  LockClosedIcon,
  EyeIcon,
  UserIcon,
  ServerIcon,
  TrashIcon,
  EnvelopeIcon,
  ChevronDownIcon,
  ArrowLeftIcon,
  BellIcon,
  CameraIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  GlobeAltIcon,
  UserGroupIcon,
  IdentificationIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  InformationCircleIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import PublicNavbar from '../components/common/PublicNavbar';
import SEOHead from '../components/common/SEOHead';
import { Capacitor } from '@capacitor/core';
import { useAppSelector } from '../hooks/redux';

const LAST_UPDATED = 'August 9, 2026';
const EFFECTIVE_DATE = 'August 9, 2026';
const CONTACT_EMAIL = 'support@clickcollab.in';
const PLATFORM_NAME = 'ClickCollab';
const WEBSITE = 'clickcollab.in';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Section {
  id: string;
  icon: React.ReactNode;
  title: string;
  badge?: string;
  content: React.ReactNode;
}

// ─── Accordion ───────────────────────────────────────────────────────────────
const AccordionItem: React.FC<{
  section: Section;
  isOpen: boolean;
  onToggle: () => void;
  index: number;
}> = ({ section, isOpen, onToggle, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.04, duration: 0.35 }}
    className="border border-gray-100 dark:border-gray-800 rounded-2xl overflow-hidden bg-white dark:bg-gray-900 shadow-sm hover:shadow-md transition-all duration-200"
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-2xl"
      aria-expanded={isOpen}
      id={`pp-${section.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 flex-shrink-0 group-hover:bg-indigo-100 dark:group-hover:bg-indigo-900/40 transition-colors">
          {section.icon}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white">{section.title}</h2>
          {section.badge && (
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400">
              {section.badge}
            </span>
          )}
        </div>
      </div>
      <ChevronDownIcon
        className={`w-4 h-4 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-3 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="content"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="px-5 pb-6 text-sm text-gray-600 dark:text-gray-400 leading-relaxed space-y-4 border-t border-gray-50 dark:border-gray-800 pt-4">
            {section.content}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

// ─── Reusable sub-components ─────────────────────────────────────────────────
const UL: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ul className="space-y-2 mt-2 pl-1">{children}</ul>
);
const LI: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
    <span>{children}</span>
  </li>
);
const Strong: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <strong className="text-gray-800 dark:text-gray-200 font-semibold">{children}</strong>
);
const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-3 p-4 bg-indigo-50 dark:bg-indigo-950/30 rounded-xl border border-indigo-100 dark:border-indigo-900/40 text-sm text-indigo-800 dark:text-indigo-300 leading-relaxed flex gap-3">
    <InformationCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-indigo-500" />
    <div>{children}</div>
  </div>
);

// ─── Main Page ────────────────────────────────────────────────────────────────
const PrivacyPolicyPage: React.FC = () => {
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [openSection, setOpenSection] = useState<string | null>('overview');

  const toggle = (id: string) => setOpenSection(prev => (prev === id ? null : id));

  const sections: Section[] = [
    // ── 1. OVERVIEW ──────────────────────────────────────────────────────────
    {
      id: 'overview',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      title: 'Overview & Scope',
      content: (
        <>
          <p>
            <Strong>{PLATFORM_NAME}</Strong> ("we", "our", "us") is a professional creative networking
            platform available at <Strong>{WEBSITE}</Strong> and as a mobile app on Android (via Capacitor).
            It connects <Strong>Creators</Strong> (photographers &amp; videographers),{' '}
            <Strong>Models</Strong>, and <Strong>Hirers</Strong> (brands &amp; businesses) for portfolio
            showcasing, collaboration, and project management.
          </p>
          <p className="mt-2">
            This Privacy Policy applies to all personal data processed through:
          </p>
          <UL>
            <LI>The <Strong>web application</Strong> at {WEBSITE}</LI>
            <LI>The <Strong>Android mobile app</Strong> built on Capacitor</LI>
            <LI>Any <Strong>public-facing pages</Strong> (Explore, Opportunity listings, Portfolio galleries, Invoice &amp; Contract portal pages)</LI>
            <LI>All <Strong>APIs and backend services</Strong> that power the platform</LI>
          </UL>
          <p className="mt-2">
            This policy is governed by the <Strong>Information Technology Act, 2000</Strong>, the{' '}
            <Strong>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</Strong>,
            and the <Strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</Strong>.
          </p>
          <InfoBox>
            By registering on {PLATFORM_NAME} or continuing to use our services, you (the <em>Data Principal</em>)
            give informed, free, specific, and unambiguous consent to the collection and processing of your
            personal data as described in this policy and agree to our Terms and Conditions.
          </InfoBox>
        </>
      ),
    },

    // ── 1B. PLATFORM AUTHORITY ON ACCOUNTS ─────────────────────────────────
    {
      id: 'account-authority',
      icon: <ShieldExclamationIcon className="w-4 h-4 text-rose-500" />,
      title: 'Platform Authority — Disabling & Permanent Account Deletion',
      badge: 'Terms & Conditions §15',
      content: (
        <>
          <p>
            <Strong>{PLATFORM_NAME} holds complete, absolute, and unreserved authority</Strong> to temporarily disable, suspend, or permanently delete any user account, profile, portfolio listing, post, CRM workspace item, or direct message on the platform at any time, for any reason or no reason, in all cases, with or without prior notice, at {PLATFORM_NAME}'s sole discretion.
          </p>
          <p className="mt-2">
            Circumstances that may trigger immediate account disablement or permanent deletion include, but are not limited to:
          </p>
          <UL>
            <LI>Violation of our Community Guidelines or Content Standards (including prohibited nudity/explicit uploads)</LI>
            <LI>Harassment, abusive behavior, spam, impersonation, or deceptive practices</LI>
            <LI>Creation of duplicate, fraudulent, or unverified accounts</LI>
            <LI>Security vulnerabilities, platform abuse, or unauthorized automated access</LI>
            <LI>Failure to verify mandatory account requirements (such as location operational boundaries)</LI>
            <LI>Legal compliance directives or law enforcement mandates</LI>
          </UL>
          <InfoBox>
            <Strong>Disclaimer of Liability:</Strong> {PLATFORM_NAME} shall not be liable to any user or third party for any suspension, restriction, temporary disablement, or permanent deletion of an account or associated user data.
          </InfoBox>
        </>
      ),
    },

    // ── 1C. PROHIBITION OF NUDITY ──────────────────────────────────────────
    {
      id: 'nudity-policy',
      icon: <ExclamationTriangleIcon className="w-4 h-4 text-amber-500" />,
      title: 'Content Standards — Strict Prohibition of Nudity & Adult Content',
      badge: 'Zero Tolerance',
      content: (
        <>
          <p>
            <Strong>There is a strict zero-tolerance policy against nudity and adult content on {PLATFORM_NAME}.</Strong>
          </p>
          <p className="mt-2">
            The uploading, posting, sharing, or transmission of any of the following material is strictly prohibited across all areas of the application:
          </p>
          <UL>
            <LI><Strong>Full or partial nudity</Strong> (including explicit or implied adult imagery)</LI>
            <LI><Strong>Sexually explicit or pornographic media</Strong> (images, videos, links, or text)</LI>
            <LI><Strong>Lewd, suggestive, or inappropriate adult photos/videos</Strong> in portfolios, cover photos, or profile pictures</LI>
            <LI><Strong>Explicit media sent via Direct Messages</Strong>, CRM project galleries, or opportunity applications</LI>
          </UL>
          <div className="mt-3 p-4 bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-900/50 rounded-xl text-xs text-rose-800 dark:text-rose-300 font-medium leading-relaxed">
            <span className="font-black uppercase tracking-wider block mb-1">🚨 Strict Enforcement Action:</span>
            Any violation of this nudity prohibition will result in <Strong>immediate and permanent deletion of the user account</Strong>, permanent purging of all associated portfolio assets, and potential reporting to law enforcement authorities.
          </div>
        </>
      ),
    },

    // ── 2. WHO WE ARE ─────────────────────────────────────────────────────────
    {
      id: 'who-we-are',
      icon: <IdentificationIcon className="w-4 h-4" />,
      title: 'Data Fiduciary — Who We Are',
      badge: 'DPDP Act §4',
      content: (
        <>
          <p>
            As a <Strong>Data Fiduciary</Strong> under the DPDP Act, 2023, we determine the purpose and
            means of processing your personal data. Our details:
          </p>
          <div className="mt-3 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden text-sm">
            {[
              ['Entity Name', 'ClickCollab'],
              ['Platform', `${WEBSITE} / Android App`],
              ['Grievance Officer Email', CONTACT_EMAIL],
              ['Response SLA', '7 business days'],
            ].map(([label, value]) => (
              <div key={label} className="flex border-b border-gray-50 dark:border-gray-800 last:border-0">
                <div className="w-48 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 text-gray-500 dark:text-gray-400 font-medium flex-shrink-0">{label}</div>
                <div className="px-4 py-3 text-gray-800 dark:text-gray-200">{value}</div>
              </div>
            ))}
          </div>
        </>
      ),
    },

    // ── 3. DATA WE COLLECT ────────────────────────────────────────────────────
    {
      id: 'data-collected',
      icon: <UserIcon className="w-4 h-4" />,
      title: 'Personal Data We Collect',
      badge: 'Comprehensive',
      content: (
        <>
          <p>We collect the following categories of data, depending on how you use the platform:</p>

          <div className="mt-3 space-y-4">
            {/* Account */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <UserIcon className="w-3.5 h-3.5 text-indigo-500" /> Account &amp; Identity Data
              </h3>
              <UL>
                <LI>First name, last name, email address, hashed password</LI>
                <LI>User type: <em>Creator (Photographer/Videographer/Both)</em>, <em>Model</em>, or <em>Hirer</em></LI>
                <LI>Phone number (optional; visibility controlled by you)</LI>
                <LI>Email verification status &amp; early-access flag</LI>
                <LI>Referral code and referred-by association</LI>
              </UL>
            </div>

            {/* Profile */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <CameraIcon className="w-3.5 h-3.5 text-indigo-500" /> Profile &amp; Portfolio Data
              </h3>
              <UL>
                <LI>Profile photo, cover photo, biography, website URL, Instagram handle</LI>
                <LI>Skills, specialisations, experience level, hourly rate, availability status</LI>
                <LI>Spoken languages, response time, collaboration count</LI>
                <LI>Portfolio images &amp; videos (uploaded files, YouTube/Vimeo/Reel links), tags, folder structure</LI>
                <LI>Showcase sections, pages, and layout items</LI>
                <LI>Portfolio engagement data: view counts, like counts, comment content, share counts</LI>
                <LI><Strong>Model-specific data</Strong> (only if you register as a Model): height, weight, bust/waist/hips measurements, shoe size, dress size, eye colour, hair colour, ethnicity, gender, half-day/full-day/usage-rights rates, agency representation, TFP/paid shoot preferences, shoot preferences, digitals</LI>
              </UL>
              <InfoBox>
                Model measurements and physical attributes are classified as <Strong>sensitive personal data</Strong> under
                the IT (SPDI) Rules, 2011. They are collected only when voluntarily provided and are used
                solely for matching you with relevant opportunities on the Explore &amp; Opportunities pages.
              </InfoBox>
            </div>

            {/* Location */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <MapPinIcon className="w-3.5 h-3.5 text-indigo-500" /> Location Data
              </h3>
              <UL>
                <LI><Strong>City/region</Strong>: Manually entered on your profile for Explore discoverability</LI>
                <LI><Strong>Precise GPS coordinates</Strong> (latitude, longitude, accuracy): Collected only when you explicitly grant browser/device location permission, used to power the "nearby opportunities" and "nearby users" features. Stored against your profile and opportunity posts with a timestamp.</LI>
                <LI><Strong>Available cities</Strong>: Cities you are willing to travel to, entered manually</LI>
              </UL>
            </div>

            {/* Communications */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <ChatBubbleLeftRightIcon className="w-3.5 h-3.5 text-indigo-500" /> Communications &amp; Collaboration Data
              </h3>
              <UL>
                <LI>Direct messages between users (content, timestamps, read status)</LI>
                <LI>Collaboration requests: type, timeline, subject, message body, budget, location, urgency, deliverables, requirements, equipment notes, preferred dates</LI>
                <LI>Collaboration project data: title, description, shoot location, date range, cover images, project images &amp; videos, collaborator roles, approval insights</LI>
                <LI>Opportunity listings: title, description, type, location, dates, budget, tags, images, contact email &amp; phone, requirements</LI>
                <LI>Opportunity applications: cover message, portfolio items submitted, decision status</LI>
                <LI>Reviews &amp; ratings written and received between users</LI>
                <LI>Notifications: system-generated and collaboration-specific notifications, read/unread status</LI>
              </UL>
            </div>

            {/* CRM */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <ClipboardDocumentListIcon className="w-3.5 h-3.5 text-indigo-500" /> CRM &amp; Business Data (Creator/Hirer Only)
              </h3>
              <UL>
                <LI><Strong>Clients</Strong>: Client name, contact details you store in your private CRM workspace</LI>
                <LI><Strong>Leads</Strong>: Lead name, source, status, notes, conversion tracking</LI>
                <LI><Strong>Projects</Strong>: Project name, status, final amount, checklist, configuration, activity log</LI>
                <LI><Strong>Invoices</Strong>: Line items, amounts, GST percentage, payment status, public shareable links</LI>
                <LI><Strong>Contracts</Strong>: Contract terms, digital signature names, signing timestamps, rejection reasons, contract templates</LI>
                <LI><Strong>Project Gallery</Strong>: Images and videos uploaded per project, client image selection records</LI>
                <LI><Strong>Calendar Events</Strong>: Event title, date, time, notes</LI>
                <LI><Strong>Business Profile</Strong>: Company name, business logo, GST number, GST percentage, registered address</LI>
              </UL>
            </div>

            {/* Payment */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <CurrencyDollarIcon className="w-3.5 h-3.5 text-indigo-500" /> Payment &amp; Subscription Data
              </h3>
              <UL>
                <LI>Razorpay order ID, payment ID, and cryptographic payment signature (used for payment verification only — no raw card/bank data is stored by us)</LI>
                <LI>Subscription tier, billing cycle, subscription expiry date</LI>
                <LI>ClickCoins balance (platform reward currency)</LI>
                <LI>Subscription invoice history</LI>
              </UL>
              <InfoBox>
                Full card numbers, CVV codes, and bank account details are <Strong>never</Strong> stored on our servers.
                All payment processing is handled directly by <Strong>Razorpay</Strong> under their PCI-DSS compliant infrastructure.
              </InfoBox>
            </div>

            {/* Social */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <GlobeAltIcon className="w-3.5 h-3.5 text-indigo-500" /> Social &amp; Third-Party Auth Data
              </h3>
              <UL>
                <LI><Strong>Google OAuth</Strong>: When you sign up or log in with Google, we receive your Google ID token / access token, name, and email from Google's servers. We do not store your Google password.</LI>
                <LI><Strong>Instagram Handle</Strong>: Your Instagram username as entered on your profile (public display only). We also support Instagram API integration for profile data — see §8 (Third-Party Services).</LI>
              </UL>
            </div>

            {/* Social Graph */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <UserGroupIcon className="w-3.5 h-3.5 text-indigo-500" /> Social Graph &amp; Engagement Data
              </h3>
              <UL>
                <LI>Follow/following relationships between users</LI>
                <LI>Collaborator relationships (users who have worked together)</LI>
                <LI>Block/report records (for safety enforcement)</LI>
                <LI>Portfolio collections (saved items)</LI>
                <LI>Explore filter presets and filter history (saved search configurations)</LI>
                <LI>Recommendation feedback (like/dislike/view/apply actions on suggested opportunities)</LI>
                <LI>Profile impression analytics (which users viewed your profile in Explore results)</LI>
              </UL>
            </div>

            {/* Device / Technical */}
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1.5">
                <ServerIcon className="w-3.5 h-3.5 text-indigo-500" /> Technical &amp; Device Data
              </h3>
              <UL>
                <LI>IP address, browser user-agent, device type (web vs. Android native via Capacitor)</LI>
                <LI>Authentication JWT tokens stored in <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">localStorage</code></LI>
                <LI>Session-level network request/response logs captured in <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">window.__NETWORK_LOGS__</code> (debug drawer — in-memory only, never sent to our servers)</LI>
                <LI>FCM (Firebase Cloud Messaging) push notification tokens for Android push delivery</LI>
                <LI>Dark/light mode preference, image quality preference, default portfolio view and tab preferences</LI>
                <LI>Support ticket content (subject and message)</LI>
              </UL>
            </div>
          </div>
        </>
      ),
    },

    // ── 4. PURPOSE & LEGAL BASIS ──────────────────────────────────────────────
    {
      id: 'purpose',
      icon: <EyeIcon className="w-4 h-4" />,
      title: 'Purpose of Processing & Legal Basis',
      badge: 'DPDP Act §4, §7',
      content: (
        <>
          <p>We process your personal data <Strong>only for specific, lawful purposes</Strong>. The table below maps each processing activity to its purpose and legal basis:</p>
          <div className="mt-3 rounded-xl border border-gray-100 dark:border-gray-800 overflow-auto text-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[10px]">
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Processing Activity</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Purpose</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Legal Basis (DPDP)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {[
                  ['Account registration & authentication', 'Create and manage your account; verify identity; secure login', 'Consent (§7(a))'],
                  ['Profile & portfolio display', 'Show your work to other users and the public on Explore/Search pages', 'Consent (§7(a))'],
                  ['Collaboration & opportunity matching', 'Connect creators with hirers; surface relevant opportunities', 'Contract performance (§7(b)) / Consent'],
                  ['CRM workspace (leads, projects, invoices, contracts)', 'Enable creators/hirers to manage their client workflow privately', 'Contract performance (§7(b)) / Consent'],
                  ['Direct messaging', 'Enable communication between platform users', 'Consent (§7(a))'],
                  ['Push notifications (FCM)', 'Deliver timely alerts for messages, requests, and collaboration updates', 'Consent (§7(a))'],
                  ['Geolocation for nearby search', 'Power the "nearby opportunities" and "nearby users" features', 'Consent (§7(a)) — explicit permission required'],
                  ['Payment processing (Razorpay)', 'Verify subscription payments; generate invoices', 'Contract performance (§7(b)) / Legitimate interest'],
                  ['Analytics & recommendations', 'Improve platform features; serve personalised recommendations', 'Legitimate interest (§7(d)) / Consent'],
                  ['Safety (block, report, abuse detection)', 'Protect users from harassment; enforce community standards', 'Legal obligation (§7(c)) / Legitimate interest'],
                  ['Legal compliance (tax, audit records)', 'Maintain financial records as required by Indian law', 'Legal obligation (§7(c))'],
                ].map(([activity, purpose, basis]) => (
                  <tr key={activity} className="text-gray-600 dark:text-gray-400 hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 align-top">{activity}</td>
                    <td className="px-4 py-3 align-top">{purpose}</td>
                    <td className="px-4 py-3 align-top whitespace-nowrap text-indigo-600 dark:text-indigo-400 font-medium">{basis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ),
    },

    // ── 5. DATA SHARING ───────────────────────────────────────────────────────
    {
      id: 'sharing',
      icon: <UserGroupIcon className="w-4 h-4" />,
      title: 'How We Share Your Data',
      content: (
        <>
          <p>We <Strong>do not sell, rent, or trade</Strong> your personal data to third parties. Data sharing occurs only in these specific, limited circumstances:</p>
          <div className="mt-3 space-y-3">
            {[
              {
                title: 'With Other Platform Users (Public Profile)',
                body: 'When you create a profile, the following is visible to all logged-in users and, where applicable, to unauthenticated visitors on public Explore/Profile pages: name, profile photo, bio, location, user type, skills, experience, hourly rate, portfolio images & videos, collaboration history, review scores, availability status, and Instagram handle (if provided). Phone numbers and email addresses are shown only if you choose to make them public in your Privacy Settings.',
              },
              {
                title: 'With CRM Clients via Public Portal Links',
                body: 'When you share a CRM invoice, contract, or project gallery link, the recipient (your client) can view the contents of that specific document. These links are token-protected. Client data stored in your CRM workspace is your private business data — we do not expose it to other users.',
              },
              {
                title: 'With Cloud & Infrastructure Service Providers',
                body: 'We use trusted third-party infrastructure providers (cloud hosting, file storage, CDN, email delivery) who process data strictly on our behalf under data processing agreements. They are contractually prohibited from using your data for their own purposes.',
              },
              {
                title: 'With Razorpay (Payment Processor)',
                body: 'Subscription payment data (order ID, payment ID, signature) is shared with Razorpay to verify and record transactions. Razorpay operates under PCI-DSS standards and the RBI\'s payment aggregator guidelines. We do not have access to your card or bank details.',
              },
              {
                title: 'With Firebase / Google (Push Notifications & Hosting)',
                body: 'Your FCM device token is shared with Firebase Cloud Messaging (Google) solely for the purpose of delivering push notifications to your Android device. The web app is hosted on Firebase Hosting.',
              },
              {
                title: 'With Instagram / Meta (If You Connect Your Account)',
                body: 'If you use the Instagram integration feature, your Instagram username and public profile data are fetched via the Meta Instagram Basic Display API or Login API. Data flows are governed by Meta\'s Platform Policy. We do not post on your behalf or access private Instagram data.',
              },
              {
                title: 'With Google Maps (Location Geocoding)',
                body: 'When you grant location permission, your GPS coordinates are reverse-geocoded to a human-readable address using the Google Maps Geocoding API. Coordinates are transmitted to Google in this process, governed by Google\'s Privacy Policy.',
              },
              {
                title: 'With Law Enforcement or Authorities',
                body: 'We may disclose your data if required by law, court order, government authority, or to protect the rights, property, or safety of ClickCollab, its users, or the public. We will notify you where legally permitted.',
              },
              {
                title: 'In Connection with Business Transactions',
                body: 'In the event of a merger, acquisition, restructuring, or sale of assets, your data may be transferred to a successor entity. We will provide notice and ensure continuity of this privacy policy\'s protections.',
              },
            ].map(({ title, body }) => (
              <div key={title} className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50">
                <p className="font-semibold text-gray-800 dark:text-gray-200 text-xs mb-1">{title}</p>
                <p className="text-gray-600 dark:text-gray-400 text-xs leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </>
      ),
    },

    // ── 6. STORAGE & SECURITY ─────────────────────────────────────────────────
    {
      id: 'storage',
      icon: <LockClosedIcon className="w-4 h-4" />,
      title: 'Data Storage, Transfer & Security',
      badge: 'IT Rules 2011 §8',
      content: (
        <>
          <p>
            <Strong>Storage Location:</Strong> Your data is primarily stored on servers located in India. Certain infrastructure services (CDN, push notifications, cloud file storage) may process or cache data in other regions. Where cross-border transfers occur, we ensure equivalent data protection safeguards are in place, consistent with the DPDP Act, 2023.
          </p>
          <p className="mt-3">
            <Strong>Security Measures:</Strong> We implement the following technical and organisational measures to protect your data:
          </p>
          <UL>
            <LI><Strong>Encryption in transit:</Strong> All API communication is over HTTPS/TLS 1.2+. JWT tokens are transmitted in Authorization headers.</LI>
            <LI><Strong>Authentication tokens:</Strong> JWT access tokens are stored in browser <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">localStorage</code>. Sensitive credential data (passwords) is stored as bcrypt hashes server-side — never in plaintext.</LI>
            <LI><Strong>Role-based access control:</Strong> Admin-only endpoints are protected by server-side admin flag verification. Users can only access their own private data (CRM, messages) unless explicitly shared.</LI>
            <LI><Strong>Public portal security:</Strong> CRM invoice, contract, and gallery links are protected by unique tokens and optional passwords. Contracts require digital signature by the authorised party.</LI>
            <LI><Strong>Rate limiting &amp; abuse prevention:</Strong> API endpoints are rate-limited to prevent brute-force and enumeration attacks.</LI>
            <LI><Strong>Token expiry &amp; revocation:</Strong> Auth tokens expire and are cleared from localStorage on 401 responses. Users can change their password to invalidate sessions.</LI>
            <LI><Strong>Image processing:</Strong> Uploaded images are processed to generate thumbnail and medium-quality versions server-side, reducing unnecessary transmission of large files.</LI>
            <LI><Strong>Scheduled account deletion:</Strong> When you request account deletion, it is flagged with a <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">deletion_scheduled_at</code> timestamp and soft-deleted before permanent purge.</LI>
          </UL>
          <InfoBox>
            No security system is impenetrable. While we implement industry-standard protections, we
            cannot guarantee absolute security. In the event of a data breach that is likely to adversely
            affect you, we will notify you as required by applicable law.
          </InfoBox>
        </>
      ),
    },

    // ── 7. YOUR RIGHTS ────────────────────────────────────────────────────────
    {
      id: 'rights',
      icon: <ShieldCheckIcon className="w-4 h-4" />,
      title: 'Your Rights as a Data Principal',
      badge: 'DPDP Act Chapter III',
      content: (
        <>
          <p>Under the Digital Personal Data Protection Act, 2023, you have the following rights as a Data Principal:</p>
          <div className="mt-3 space-y-3">
            {[
              {
                right: 'Right to Access Information (§11)',
                desc: 'You may request a summary of personal data we hold about you and the processing activities carried out on it. Log in to your account to view and manage most data directly.',
                howTo: 'Email support@clickcollab.in with subject "Data Access Request".',
              },
              {
                right: 'Right to Correction & Erasure (§12)',
                desc: 'You may request correction of inaccurate or incomplete personal data, or request erasure of data that is no longer necessary. Most profile data can be edited directly from your Profile Edit or Settings pages.',
                howTo: 'For data not editable in-app, email us with subject "Data Correction/Erasure Request".',
              },
              {
                right: 'Right to Grievance Redressal (§13)',
                desc: 'You may raise a complaint or concern about how your data is being handled. We will acknowledge within 72 hours and resolve within 7 business days.',
                howTo: 'Email support@clickcollab.in — subject "Privacy Grievance".',
              },
              {
                right: 'Right to Withdraw Consent (§6)',
                desc: 'You may withdraw consent at any time. Withdrawal does not affect the lawfulness of processing based on consent before its withdrawal. Some core features may not be available after withdrawal.',
                howTo: 'Delete your account from Settings → Account → Delete Account, or email us.',
              },
              {
                right: 'Right to Nominate (§14)',
                desc: 'You may nominate another individual to exercise your rights in the event of your death or incapacity.',
                howTo: 'Email us with your nominee\'s name and contact details.',
              },
              {
                right: 'Right to Data Portability (§12)',
                desc: 'You may request a machine-readable export of your CRM data (leads, clients, projects, invoices). Use the in-app "Export CRM Data" feature in your CRM workspace.',
                howTo: 'CRM → Settings → Export Data (CSV download).',
              },
            ].map(({ right, desc, howTo }) => (
              <div key={right} className="p-4 rounded-xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
                <p className="font-semibold text-gray-800 dark:text-gray-200 text-xs mb-1">{right}</p>
                <p className="text-gray-600 dark:text-gray-400 text-xs mb-2">{desc}</p>
                <p className="text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">
                  → {howTo}
                </p>
              </div>
            ))}
          </div>
          <InfoBox>
            We will process rights requests free of charge and respond within <Strong>7 business days</Strong>.
            For complex requests, we may extend this to 30 days with prior notice.
          </InfoBox>
        </>
      ),
    },

    // ── 8. RETENTION ──────────────────────────────────────────────────────────
    {
      id: 'retention',
      icon: <TrashIcon className="w-4 h-4" />,
      title: 'Data Retention',
      content: (
        <>
          <p>We retain your data only as long as necessary for the stated purpose or as required by law:</p>
          <div className="mt-3 rounded-xl border border-gray-100 dark:border-gray-800 overflow-auto text-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 uppercase tracking-wide text-[10px]">
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Data Category</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Retention Period</th>
                  <th className="px-4 py-3 font-semibold border-b border-gray-100 dark:border-gray-800">Action on Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800 text-gray-600 dark:text-gray-400">
                {[
                  ['Active account profile & portfolio', 'Duration of account activity', 'Purged within 30 days of account deletion'],
                  ['Messages & conversations', 'Duration of account + 90 days post-deletion', 'Permanently deleted after 90-day grace period'],
                  ['Collaboration records', 'Duration of account + 90 days', 'Permanently deleted'],
                  ['CRM data (leads, clients, projects)', 'Duration of account + 90 days', 'Permanently deleted (unless legally required otherwise)'],
                  ['Invoices & financial records', 'Minimum 7 years (per Indian accounting law)', 'Archived in anonymised form, then purged'],
                  ['Signed contracts', 'Minimum 7 years (legal requirement)', 'Archived, then purged'],
                  ['Payment records (Razorpay IDs)', 'Minimum 7 years', 'Retained for tax/audit purposes'],
                  ['Auth tokens (localStorage)', 'Until logout / token expiry / account deletion', 'Cleared automatically on 401 or manual logout'],
                  ['FCM push tokens', 'Duration of app installation / account activity', 'Revoked on logout or deletion request'],
                  ['Aggregated analytics', 'Indefinitely (anonymised — no longer personal data)', 'N/A'],
                  ['Support ticket content', '3 years from ticket closure', 'Permanently deleted'],
                  ['Block & report records', 'Duration of account + 2 years', 'Reviewed and purged'],
                ].map(([cat, period, action]) => (
                  <tr key={cat} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/30">
                    <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300 align-top">{cat}</td>
                    <td className="px-4 py-3 align-top">{period}</td>
                    <td className="px-4 py-3 align-top">{action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ),
    },

    // ── 9. THIRD PARTY ────────────────────────────────────────────────────────
    {
      id: 'third-party',
      icon: <GlobeAltIcon className="w-4 h-4" />,
      title: 'Third-Party Services & Sub-Processors',
      content: (
        <>
          <p>The following third-party services are integrated into {PLATFORM_NAME}. Each is governed by their own privacy policy:</p>
          <div className="mt-3 space-y-2">
            {[
              {
                name: 'Google OAuth / Google Identity Services',
                purpose: 'Authentication — "Sign in with Google" on web and Android',
                data: 'Google ID token, access token, name, email',
                link: 'https://policies.google.com/privacy',
              },
              {
                name: 'Firebase Cloud Messaging (FCM)',
                purpose: 'Push notifications on Android app',
                data: 'Device FCM token',
                link: 'https://firebase.google.com/support/privacy',
              },
              {
                name: 'Firebase Hosting (Google)',
                purpose: 'Web app hosting and CDN',
                data: 'IP address, request logs',
                link: 'https://firebase.google.com/support/privacy',
              },
              {
                name: 'Instagram / Meta (Basic Display API)',
                purpose: 'Instagram profile integration (opt-in)',
                data: 'Instagram username, profile data, public media metadata',
                link: 'https://privacycenter.instagram.com/policy',
              },
              {
                name: 'Google Maps / Geocoding API',
                purpose: 'Convert GPS coordinates to human-readable location names',
                data: 'Latitude & longitude (when you grant location permission)',
                link: 'https://policies.google.com/privacy',
              },
              {
                name: 'Razorpay',
                purpose: 'Subscription payment processing (currently paused — platform is free)',
                data: 'Order ID, payment ID, signature for verification',
                link: 'https://razorpay.com/privacy/',
              },
              {
                name: 'Capacitor (by Ionic)',
                purpose: 'Android app runtime — bridges web app to native device APIs',
                data: 'Device info, push notification access (no data sent to Ionic)',
                link: 'https://ionic.io/privacy',
              },
            ].map(({ name, purpose, data, link }) => (
              <div key={name} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{name}</span>
                  <a href={link} target="_blank" rel="noreferrer" className="text-indigo-600 dark:text-indigo-400 hover:underline text-[10px]">Privacy Policy ↗</a>
                </div>
                <p className="text-gray-500 dark:text-gray-400"><span className="text-gray-600 dark:text-gray-300">Purpose:</span> {purpose}</p>
                <p className="text-gray-500 dark:text-gray-400"><span className="text-gray-600 dark:text-gray-300">Data shared:</span> {data}</p>
              </div>
            ))}
          </div>
        </>
      ),
    },

    // ── 10. COOKIES ───────────────────────────────────────────────────────────
    {
      id: 'cookies',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      title: 'Cookies & Local Storage',
      content: (
        <>
          <p>{PLATFORM_NAME} uses browser storage mechanisms for the following purposes:</p>
          <div className="mt-3 space-y-2">
            {[
              { name: 'JWT Auth Token', storage: 'localStorage', category: 'Essential', purpose: 'Authenticates your session on every API request. Without this, you cannot log in.', canDisable: false },
              { name: 'User Object Cache', storage: 'localStorage', category: 'Essential', purpose: 'Caches your basic user profile to reduce API calls and speed up page loads.', canDisable: false },
              { name: 'Image Cache (URL map)', storage: 'sessionStorage / memory', category: 'Performance', purpose: 'Caches resolved S3 signed image URLs to avoid redundant network requests within a session.', canDisable: true },
              { name: 'Theme Preference', storage: 'localStorage (via ThemeContext)', category: 'Functional', purpose: 'Remembers your dark/light mode preference.', canDisable: true },
              { name: 'Firebase Analytics (Vercel Analytics)', storage: 'Cookies / sessionStorage', category: 'Analytics (opt-out)', purpose: 'Tracks page views and usage patterns for product improvement. Currently disabled in development.', canDisable: true },
            ].map(({ name, storage, category, purpose, canDisable }) => (
              <div key={name} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-700/50 text-xs">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{name}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${category === 'Essential' ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : category === 'Analytics (opt-out)' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'}`}>
                    {category}
                  </span>
                  {!canDisable && <span className="text-[10px] text-gray-400">Cannot be disabled</span>}
                </div>
                <p className="text-gray-500 dark:text-gray-400"><span className="text-gray-600 dark:text-gray-300">Storage:</span> {storage}</p>
                <p className="text-gray-500 dark:text-gray-400 mt-0.5">{purpose}</p>
              </div>
            ))}
          </div>
          <InfoBox>
            We do not use third-party tracking pixels or cross-site advertising cookies.
            You can clear localStorage at any time through your browser's developer tools or settings,
            though this will log you out.
          </InfoBox>
        </>
      ),
    },

    // ── 11. NOTIFICATIONS ─────────────────────────────────────────────────────
    {
      id: 'notifications',
      icon: <BellIcon className="w-4 h-4" />,
      title: 'Push Notifications & Email Communication',
      content: (
        <>
          <UL>
            <LI><Strong>Push Notifications (Android):</Strong> We request permission to send push notifications when you use the Android app. Notifications include: new messages, collaboration invites/updates, new opportunity applications, and system alerts. You can disable push notifications at any time in your device settings or in Settings → Notifications on the platform.</LI>
            <LI><Strong>Email Notifications:</Strong> We may send transactional emails for: account verification, password reset, collaboration-related updates, and CRM document delivery (invoices, contracts). You can manage email notification preferences in Settings → Notifications. We do not send marketing emails without your explicit consent.</LI>
            <LI><Strong>In-App Notifications:</Strong> Notifications within the platform are always enabled as part of core functionality. You can mark them as read or delete them.</LI>
          </UL>
        </>
      ),
    },

    // ── 12. CHILDREN ──────────────────────────────────────────────────────────
    {
      id: 'children',
      icon: <ShieldCheckIcon className="w-4 h-4" />,
      title: "Children's Privacy",
      badge: 'DPDP Act §9',
      content: (
        <>
          <p>
            <Strong>{PLATFORM_NAME} is strictly for users aged 18 and above.</Strong> We do not knowingly
            collect personal data from minors. The DPDP Act, 2023 requires verifiable parental consent for
            processing data of children under 18.
          </p>
          <p className="mt-2">
            If you believe a minor has created an account on our platform, please notify us immediately at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 dark:text-indigo-400 hover:underline">{CONTACT_EMAIL}</a>.
            We will promptly investigate and delete the account and all associated data.
          </p>
          <p className="mt-2">
            During registration, we require users to confirm they are 18+ by accepting our Terms of Service.
            We do not implement algorithmic targeting or behavioural advertising directed at children.
          </p>
        </>
      ),
    },

    // ── 13. ACCOUNT DELETION ─────────────────────────────────────────────────
    {
      id: 'deletion',
      icon: <TrashIcon className="w-4 h-4" />,
      title: 'Account Deletion & Data Removal',
      content: (
        <>
          <p>You can request permanent deletion of your account and associated personal data at any time:</p>
          <div className="mt-3 p-4 bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/40 rounded-xl text-xs space-y-2">
            <p className="font-bold text-red-700 dark:text-red-400">⚠ This action is irreversible after the grace period.</p>
            <p className="text-red-600 dark:text-red-400">Deletion process: Settings → Account → Delete Account → Confirm</p>
          </div>
          <p className="mt-3">Upon account deletion:</p>
          <UL>
            <LI>Your profile is immediately hidden from Explore and search results</LI>
            <LI>Profile data, portfolio images/videos, posts, and preferences are purged within <Strong>30 days</Strong></LI>
            <LI>CRM data (clients, leads, projects) is purged within <Strong>90 days</Strong></LI>
            <LI>Messages are purged within <Strong>90 days</Strong></LI>
            <LI>Financial records (invoices, contracts, payment references) are retained for <Strong>7 years</Strong> per Indian tax and accounting law</LI>
            <LI>Your account's <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">deletion_scheduled_at</code> timestamp is set, allowing reactivation within 30 days if you change your mind (by logging back in)</LI>
          </UL>
        </>
      ),
    },

    // ── 14. POLICY CHANGES ────────────────────────────────────────────────────
    {
      id: 'changes',
      icon: <DocumentTextIcon className="w-4 h-4" />,
      title: 'Changes to This Privacy Policy',
      content: (
        <>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, platform
            features, legal requirements, or regulatory guidance. When we make material changes, we will:
          </p>
          <UL>
            <LI>Update the "Last Updated" date at the top of this page</LI>
            <LI>Send an in-app notification and/or email notification to registered users</LI>
            <LI>For significant changes affecting sensitive data processing, seek renewed consent where required by the DPDP Act</LI>
          </UL>
          <p className="mt-3">
            Your continued use of {PLATFORM_NAME} after the effective date of any changes constitutes
            acceptance of the updated policy. If you disagree, you may delete your account as described in
            §13.
          </p>
        </>
      ),
    },

    // ── 15. CONTACT ───────────────────────────────────────────────────────────
    {
      id: 'contact',
      icon: <EnvelopeIcon className="w-4 h-4" />,
      title: 'Contact, Grievances & Data Requests',
      badge: 'Respond in 7 days',
      content: (
        <>
          <p>
            For any privacy-related queries, rights requests, or grievances, please contact our designated
            Grievance Officer as required under Section 13 of the DPDP Act, 2023:
          </p>
          <div className="mt-4 p-5 bg-indigo-50 dark:bg-indigo-950/30 rounded-2xl border border-indigo-100 dark:border-indigo-900/40 space-y-2">
            <p className="font-bold text-gray-900 dark:text-white">{PLATFORM_NAME} — Privacy &amp; Grievance Officer</p>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold block"
            >
              {CONTACT_EMAIL}
            </a>
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5 pt-1 border-t border-indigo-100 dark:border-indigo-900/30 mt-3">
              <p>📧 Use subject line: <em>"Privacy Grievance"</em> / <em>"Data Access Request"</em> / <em>"Data Deletion Request"</em></p>
              <p>⏱ Acknowledgement: within 72 hours</p>
              <p>✅ Resolution: within 7 business days (extendable to 30 for complex requests)</p>
              <p>🏛 If unresolved, you may escalate to the <Strong>Data Protection Board of India</Strong> (to be constituted under the DPDP Act, 2023)</p>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEOHead
        title={`Privacy Policy & Terms of Service — ${PLATFORM_NAME}`}
        description="Full Privacy Policy and Terms of Service for ClickCollab — India's creative professional networking platform. Includes content standards, zero nudity policy, and platform account authority."
      />
      <PublicNavbar />

      {/* ── Hero ── */}
      <div className="relative overflow-hidden bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-100/50 dark:bg-indigo-900/10 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-purple-100/30 dark:bg-purple-900/10 blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center text-center"
          >
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-600 to-indigo-700 flex items-center justify-center mb-5 shadow-xl shadow-indigo-500/30">
              <ShieldCheckIcon className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white tracking-tight mb-3">
              Privacy Policy &amp; Terms of Service
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-base max-w-xl">
              A transparent, comprehensive account of {PLATFORM_NAME}'s Terms of Service, Privacy Policy, Zero Nudity standards, and Account Management policies.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-5 text-xs">
              {[
                { label: `Effective: ${EFFECTIVE_DATE}`, color: 'gray' },
                { label: `Updated: ${LAST_UPDATED}`, color: 'gray' },
                { label: 'DPDP Act 2023', color: 'indigo' },
                { label: 'IT Act 2000', color: 'indigo' },
                { label: 'IT (SPDI) Rules 2011', color: 'indigo' },
              ].map(({ label, color }) => (
                <span
                  key={label}
                  className={`px-3 py-1.5 rounded-full font-medium ${
                    color === 'indigo'
                      ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                  }`}
                >
                  {label}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">

        {/* Intro summary box */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 p-5 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm"
        >
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            <Strong>{PLATFORM_NAME}</Strong> operates the web app and Android app available at{' '}
            <Strong>{WEBSITE}</Strong>. We are a platform for creative professionals — photographers,
            videographers, models, and brands — to discover, collaborate, and manage their creative work.
            This policy explains exactly what data we collect, why, how it is used, who it is shared with,
            and what rights you have over it. This policy applies to all users regardless of their role on
            the platform (Creator, Model, or Hirer).
          </p>
        </motion.div>

        {/* Jump links */}
        <div className="mb-6 p-4 bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm">
          <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">Jump to Section</p>
          <div className="flex flex-wrap gap-2">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => { setOpenSection(s.id); document.getElementById(`pp-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}
                className="text-xs px-3 py-1.5 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 hover:text-indigo-700 dark:hover:text-indigo-300 text-gray-600 dark:text-gray-400 font-medium transition-colors"
              >
                {s.title.replace(/^\d+\.\s/, '')}
              </button>
            ))}
          </div>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {sections.map((section, index) => (
            <AccordionItem
              key={section.id}
              section={section}
              isOpen={openSection === section.id}
              onToggle={() => toggle(section.id)}
              index={index}
            />
          ))}
        </div>

        {/* Footer CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center space-y-4"
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Questions about this policy?{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 dark:text-indigo-400 hover:underline font-semibold">
              {CONTACT_EMAIL}
            </a>
          </p>
          <Link
            to={Capacitor.isNativePlatform() ? (isAuthenticated ? "/dashboard" : "/login") : "/"}
            id="back-to-home-privacy-btn"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold rounded-xl hover:bg-gray-700 dark:hover:bg-gray-200 transition-colors text-sm"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {Capacitor.isNativePlatform() ? (isAuthenticated ? "Back to Dashboard" : "Back to Login") : "Back to Home"}
          </Link>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-100 dark:border-gray-800 mt-4 py-8 text-center text-xs text-gray-400 dark:text-gray-500">
        © {new Date().getFullYear()} {PLATFORM_NAME}. All rights reserved.
        <span className="mx-2">·</span>
        <Link to="/privacy-policy" className="hover:underline">Privacy Policy</Link>
        <span className="mx-2">·</span>
        <Link to="/contact" className="hover:underline">Contact</Link>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
